import React, {ReactNode} from 'react';
import {times} from 'lodash';
import classNames from 'classnames';
import DayDropdown from 'shared/components/day-dropdown';
import MonthDropdown from 'shared/components/month-dropdown';
import YearDropdown from 'src/components/year-dropdown';
import Fieldset from 'shared/components/fieldset';
import {daysInMonth} from 'shared/utils';

interface CommonProps {
  month: number;
  monthLabel?: string;
  monthOnChange: (value: string) => void;
  monthError?: string;
  months?: {value: string; text: string}[];

  year: number;
  yearLabel?: string;
  yearOnChange: (value: string) => void;
  yearError?: string;
  lastX?: number;
  years?: {value: string; text: string}[];

  onChange?: (month: number, year: number, day: number) => void;
  maxHeight?: number;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
}

interface PropsWithDay extends CommonProps {
  includeDay: true; // used to be named dayOptional
  day: number;
  dayLabel?: string;
  dayOnChange: (value: string) => void;
  dayError?: string;
  days?: {value: string; text: string}[];
}

export type Props = PropsWithDay | PropsWithoutDay;

export interface PropsWithoutDay extends CommonProps {
  includeDay?: undefined;
  day?: undefined;
  dayLabel?: undefined;
  dayOnChange?: undefined;
  dayError?: undefined;
  days?: undefined;
}

const DatePicker = ({
  includeDay,
  day,
  month,
  year,
  maxHeight = 200,
  lastX,
  dayLabel,
  monthLabel,
  yearLabel,
  label,
  required,
  disabled,
  onChange,
  dayOnChange,
  monthOnChange,
  yearOnChange,
  monthError,
  yearError,
  dayError,
  years,
  months,
  days,
}: Props) => {
  let dayDropdown: React.ReactNode = null;

  if (includeDay) {
    const shownDays = daysInMonth(month || 0, year || new Date().getFullYear());
    const defaultDays = times(shownDays).map((i) => {
      const value = (i + 1).toString();
      return {value, text: value};
    });

    dayDropdown = (
      <div className="shared-date-picker-selection">
        <DayDropdown
          required={required}
          value={day}
          label={dayLabel}
          maxHeight={maxHeight}
          days={days || defaultDays}
          onChange={(e) => {
            dayOnChange(e.target.value);

            if (onChange) {
              onChange(month, year, e.target.value);
            }
          }}
          disabled={disabled}
          error={dayError}
        />
      </div>
    );
  }

  return (
    <div className="shared-date-picker">
      <Fieldset className={classNames({'mt-0': !label})}>
        {label && <Fieldset.Legend>{label}</Fieldset.Legend>}
        <div
          className={classNames('shared-date-picker-single-selection-group', {
            '--has-day': includeDay,
            '--has-label': label,
          })}
        >
          <div className="shared-date-picker-selection">
            <MonthDropdown
              required={required}
              months={months}
              value={month}
              label={monthLabel}
              maxHeight={maxHeight}
              onChange={(e) => {
                monthOnChange(e.target.value);

                if (onChange) {
                  onChange(e.target.value, year, day || 1);
                }
              }}
              disabled={disabled}
              error={monthError}
            />
          </div>
          {dayDropdown}
          <div className="shared-date-picker-selection">
            <YearDropdown
              required={required}
              value={year}
              lastX={lastX}
              years={years}
              label={yearLabel}
              maxHeight={maxHeight}
              onChange={(e) => {
                yearOnChange(e.target.value);

                if (onChange) {
                  onChange(month, e.target.value, day || 1);
                }
              }}
              disabled={disabled}
              error={yearError}
            />
          </div>
        </div>
      </Fieldset>
    </div>
  );
};

export default DatePicker;
